import {
  BufferGeometry,
  type Color,
  DynamicDrawUsage,
  Float32BufferAttribute,
  Group,
  InstancedMesh,
  Matrix4,
  Quaternion,
  Shape,
  ShapeGeometry,
  Vector3,
} from 'three';
import { hash01 } from '../geo';
import { mix, PAL } from '../palette';
import { toonMaterial } from '../toon';

/**
 * Spectators as flat cardboard cutouts that bounce and wave. Each cutout is a
 * flat shape in the local YZ plane facing +x; the waving arm (sometimes with a
 * flag) is a second instanced mesh pivoting at the shoulder.
 */
export interface Spectator {
  x: number;
  y: number;
  z: number;
  yaw: number;
  scale: number;
  variant: number;
  /** Seated in the grandstand: upper body only visible. */
  seated?: boolean;
}

const SHIRTS: Color[] = [PAL.red, PAL.cream, PAL.olive, PAL.ochre, PAL.terracotta, PAL.cypress, mix('asphalt', 'cream', 0.2), PAL.red];
const SKINS: Color[] = [mix('terracotta', 'cream', 0.38), PAL.terracotta, mix('brown', 'terracotta', 0.45)];
const VARIANTS = 12;

function shapeGeo(shape: Shape, color: Color, z: number): BufferGeometry {
  const g = new ShapeGeometry(shape, 6).toNonIndexed();
  g.translate(0, 0, z);
  const n = g.getAttribute('position').count;
  const col: number[] = [];
  for (let i = 0; i < n; i++) col.push(color.r, color.g, color.b);
  g.setAttribute('color', new Float32BufferAttribute(col, 3));
  return g;
}

function merge(geos: BufferGeometry[]): BufferGeometry {
  const pos: number[] = [];
  const col: number[] = [];
  for (const g of geos) {
    const p = g.getAttribute('position');
    const c = g.getAttribute('color');
    for (let i = 0; i < p.count; i++) {
      pos.push(p.getX(i), p.getY(i), p.getZ(i));
      col.push(c.getX(i), c.getY(i), c.getZ(i));
    }
  }
  const out = new BufferGeometry();
  out.setAttribute('position', new Float32BufferAttribute(pos, 3));
  out.setAttribute('color', new Float32BufferAttribute(col, 3));
  // Face +x: the shape is drawn in XY facing +z.
  out.rotateY(Math.PI / 2);
  out.computeVertexNormals();
  return out;
}

function rect(x0: number, y0: number, x1: number, y1: number): Shape {
  const s = new Shape();
  s.moveTo(x0, y0);
  s.lineTo(x1, y0);
  s.lineTo(x1, y1);
  s.lineTo(x0, y1);
  s.closePath();
  return s;
}

function circle(cx: number, cy: number, r: number): Shape {
  const s = new Shape();
  s.absarc(cx, cy, r, 0, Math.PI * 2, false);
  return s;
}

function bodyVariant(v: number): BufferGeometry {
  const shirt = SHIRTS[v % SHIRTS.length];
  const skin = SKINS[Math.floor(v / 4) % SKINS.length];
  const hat = v % 3;
  const parts: BufferGeometry[] = [];
  parts.push(shapeGeo(rect(-0.17, 0, 0.17, 0.82), PAL.brown, 0));
  const torso = new Shape();
  torso.moveTo(-0.25, 0.78);
  torso.lineTo(0.25, 0.78);
  torso.lineTo(0.31, 1.36);
  torso.quadraticCurveTo(0, 1.44, -0.31, 1.36);
  torso.closePath();
  parts.push(shapeGeo(torso, shirt, 0.001));
  // Lowered arm on the far side.
  parts.push(shapeGeo(rect(-0.37, 0.86, -0.25, 1.34), shirt, 0.002));
  parts.push(shapeGeo(circle(0, 1.64, 0.22), skin, 0.003));
  if (hat === 0) {
    // Straw hat.
    parts.push(shapeGeo(rect(-0.34, 1.76, 0.34, 1.82), PAL.ochre, 0.004));
    parts.push(shapeGeo(rect(-0.18, 1.8, 0.18, 1.97), PAL.ochre, 0.005));
  } else if (hat === 1) {
    const hair = new Shape();
    hair.absarc(0, 1.66, 0.225, 0.2, Math.PI - 0.2, false);
    hair.closePath();
    parts.push(shapeGeo(hair, PAL.brown, 0.004));
  } else {
    parts.push(shapeGeo(rect(-0.22, 1.76, 0.22, 1.86), PAL.cream, 0.004));
    parts.push(shapeGeo(rect(-0.2, 1.84, 0.12, 1.92), PAL.cream, 0.005));
  }
  return merge(parts);
}

function armVariant(flag: number): BufferGeometry {
  // Pivot at the origin (the shoulder); arm points up (+y).
  const parts: BufferGeometry[] = [];
  parts.push(shapeGeo(rect(-0.055, 0, 0.055, 0.58), PAL.cream, 0));
  parts.push(shapeGeo(circle(0, 0.62, 0.075), mix('terracotta', 'cream', 0.38), 0.001));
  if (flag > 0) {
    parts.push(shapeGeo(rect(-0.015, 0.55, 0.015, 1.25), PAL.brown, 0.002));
    parts.push(shapeGeo(rect(0.015, 0.85, 0.62, 1.24), flag === 1 ? PAL.red : PAL.cream, 0.003));
  }
  return merge(parts);
}

export class Crowd {
  readonly group = new Group();
  private bodies: { mesh: InstancedMesh; who: Spectator[] }[] = [];
  private arms: { mesh: InstancedMesh; who: Spectator[] }[] = [];
  private m = new Matrix4();
  private q = new Quaternion();
  private p = new Vector3();
  private s = new Vector3();
  private up = new Vector3(0, 1, 0);
  private rollAxis = new Vector3(1, 0, 0);
  private qa = new Quaternion();

  constructor(people: Spectator[]) {
    const mat = toonMaterial(0xffffff, { vertexColors: true, doubleSide: true });
    const byBody: Spectator[][] = Array.from({ length: VARIANTS }, () => []);
    const byArm: Spectator[][] = [[], [], []];
    for (const s of people) {
      byBody[s.variant % VARIANTS].push(s);
      byArm[Math.floor(hash01(s.variant, Math.round(s.x * 10)) * 3)].push(s);
    }
    byBody.forEach((who, v) => {
      if (!who.length) return;
      const mesh = new InstancedMesh(bodyVariant(v), mat, who.length);
      mesh.instanceMatrix.setUsage(DynamicDrawUsage);
      mesh.castShadow = true;
      mesh.frustumCulled = false;
      this.bodies.push({ mesh, who });
      this.group.add(mesh);
    });
    byArm.forEach((who, v) => {
      if (!who.length) return;
      const mesh = new InstancedMesh(armVariant(v), mat, who.length);
      mesh.instanceMatrix.setUsage(DynamicDrawUsage);
      mesh.castShadow = true;
      mesh.frustumCulled = false;
      this.arms.push({ mesh, who });
      this.group.add(mesh);
    });
    this.update(0, 0);
  }

  /** excitement 0..1: louder when the car is near / crossing the line. */
  update(t: number, excitement: number): void {
    const hop = 0.08 + excitement * 0.12;
    for (const { mesh, who } of this.bodies) {
      for (let i = 0; i < who.length; i++) {
        const s = who[i];
        const ph = hash01(i, s.variant * 7 + 3) * 6.28;
        const f = 5 + hash01(i, s.variant) * 4;
        const b = Math.abs(Math.sin(t * f + ph)) * hop * s.scale;
        const sway = Math.sin(t * f * 0.5 + ph) * 0.06;
        this.q.setFromAxisAngle(this.up, s.yaw);
        this.qa.setFromAxisAngle(this.rollAxis, sway);
        this.q.multiply(this.qa);
        this.p.set(s.x, s.y + b, s.z);
        this.s.set(s.scale, s.scale, s.scale);
        this.m.compose(this.p, this.q, this.s);
        mesh.setMatrixAt(i, this.m);
      }
      mesh.instanceMatrix.needsUpdate = true;
    }
    for (const { mesh, who } of this.arms) {
      for (let i = 0; i < who.length; i++) {
        const s = who[i];
        const ph = hash01(i, 77 + s.variant) * 6.28;
        const f = 5 + hash01(i, s.variant) * 4;
        const b = Math.abs(Math.sin(t * f + ph)) * hop * s.scale;
        const wave = 0.35 + Math.sin(t * (6 + excitement * 4) + ph) * (0.45 + excitement * 0.2);
        this.q.setFromAxisAngle(this.up, s.yaw);
        // Shoulder is on the near side (+z of the cutout before rotation -> local -x... keep simple: offset along the cutout plane).
        const sx = Math.sin(s.yaw) * -0.29 * s.scale;
        const sz = Math.cos(s.yaw) * -0.29 * s.scale;
        this.qa.setFromAxisAngle(this.rollAxis, -wave);
        this.q.multiply(this.qa);
        this.p.set(s.x + sx, s.y + b + 1.3 * s.scale, s.z + sz);
        this.s.set(s.scale, s.scale, s.scale);
        this.m.compose(this.p, this.q, this.s);
        mesh.setMatrixAt(i, this.m);
      }
      mesh.instanceMatrix.needsUpdate = true;
    }
  }
}
